import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, Building2, CalendarCheck, CalendarDays, Inbox, PlusCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatINR, fmtErr, LEAD_STATUS_LABELS, LEAD_STATUS_COLORS, LISTING_STATUS_COLORS } from "../../lib/api";
import DashboardLayout, { StatCard, EmptyState, Th, Td } from "./DashboardLayout";
import PropertyCard from "../../components/PropertyCard";
import ListingForm from "../../components/ListingForm";
import { fmtSlot } from "./VendorDashboard";
import VisitCalendar from "./VisitCalendar";

function BuyerRescheduleControl({ lead, onDone }) {
  const [slot, setSlot] = useState("");
  const sv = lead.site_visit || {};
  if (sv.status !== "confirmed") return null;
  const rs = sv.reschedule;

  const propose = async () => {
    if (!slot) return toast.error("Pick a date & time first");
    try {
      await api.post(`/leads/${lead.id}/visits/reschedule`, { slot });
      toast.success("New time proposed — your host has been notified");
      setSlot("");
      onDone();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const accept = async () => {
    try {
      await api.post(`/leads/${lead.id}/visits/confirm`, { slot: rs.slot });
      toast.success("New time confirmed — see you there.");
      onDone();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  if (rs?.side === "host") {
    return (
      <div className="mt-1.5 flex items-center gap-2" data-testid={`reschedule-request-${lead.id}`}>
        <span className="text-[11px] text-black/60">Host asks: <strong>{fmtSlot(rs.slot)}</strong></span>
        <button className="dash-btn-ghost" onClick={accept} data-testid={`reschedule-accept-${lead.id}`}>Accept</button>
      </div>
    );
  }
  if (rs?.side === "buyer") {
    return <p className="mt-1.5 text-[11px] text-black/45" data-testid={`reschedule-waiting-${lead.id}`}>You proposed {fmtSlot(rs.slot)} — awaiting host</p>;
  }
  return (
    <div className="mt-1.5 flex items-center gap-1.5">
      <input
        type="datetime-local"
        className="dash-input w-40 px-2 py-1 text-[11px]"
        value={slot}
        onChange={(e) => setSlot(e.target.value)}
        data-testid={`reschedule-input-${lead.id}`}
      />
      <button className="dash-btn-ghost" onClick={propose} data-testid={`reschedule-propose-${lead.id}`}>New time</button>
    </div>
  );
}

export default function BuyerDashboard() {
  const [tab, setTab] = useState("saved");
  const [stats, setStats] = useState(null);
  const [saved, setSaved] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [listings, setListings] = useState([]);
  const [formState, setFormState] = useState(null); // null | "new" | property object

  const loadStats = useCallback(() => api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {}), []);
  const loadSaved = useCallback(() => api.get("/saved").then(({ data }) => setSaved(data.properties)).catch(() => {}), []);
  const loadEnquiries = useCallback(() => api.get("/leads").then(({ data }) => setEnquiries(data.leads)).catch(() => {}), []);
  const loadListings = useCallback(() => api.get("/properties/my/listings").then(({ data }) => setListings(data.properties)).catch(() => {}), []);

  useEffect(() => { loadStats(); loadSaved(); loadEnquiries(); }, [loadStats, loadSaved, loadEnquiries]);
  useEffect(() => { if (tab === "my-listings") loadListings(); }, [tab, loadListings]);

  const reload = () => { loadEnquiries(); loadStats(); };

  const unsave = async (id) => {
    try {
      await api.delete(`/saved/${id}`);
      toast.success("Removed from your collection");
      loadSaved();
      loadStats();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const confirmVisit = async (leadId, slot) => {
    try {
      await api.post(`/leads/${leadId}/visits/confirm`, { slot });
      toast.success("Visit confirmed — see you there.");
      reload();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const upcoming = enquiries
    .filter((l) => l.site_visit?.status === "confirmed" && l.site_visit?.confirmed?.slot)
    .sort((a, b) => new Date(a.site_visit.confirmed.slot) - new Date(b.site_visit.confirmed.slot))
    .filter((l) => new Date(l.site_visit.confirmed.slot) >= new Date(new Date().toDateString()))[0];

  return (
    <DashboardLayout
      title="My Collection"
      activeTab={tab}
      onTabChange={setTab}
      tabs={[
        { key: "saved", label: "Saved Residences", icon: Bookmark, badge: stats?.saved || 0 },
        { key: "enquiries", label: "My Enquiries", icon: Inbox },
        { key: "visits", label: "My Visits", icon: CalendarDays },
        { key: "my-listings", label: "My Listings", icon: Building2 },
      ]}
    >
      {upcoming && (
        <div className="mb-6 flex flex-wrap items-center gap-4 border border-black bg-black px-6 py-5 text-white" data-testid="upcoming-visit-card">
          <CalendarCheck size={22} className="text-gold" />
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50">Upcoming walkthrough</p>
            <p className="mt-1 font-serif text-xl">{upcoming.property_title} · {fmtSlot(upcoming.site_visit.confirmed.slot)}</p>
            <p className="text-[11px] text-white/55">{upcoming.property_locality} · host {upcoming.assigned_to_name || "Gurgaon Estates desk"}</p>
          </div>
          <Link to="/listings" className="ml-auto hidden border border-white/30 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors hover:border-gold hover:text-gold sm:block" data-testid="upcoming-browse-link">
            Keep browsing
          </Link>
        </div>
      )}

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="buyer-stats">
        <StatCard testid="stat-saved" label="Saved" value={stats?.saved || 0} />
        <StatCard testid="stat-enquiries" label="Enquiries" value={stats?.enquiries || 0} />
      </div>

      {tab === "saved" && (
        <div data-testid="saved-panel">
          {saved.length === 0 ? (
            <EmptyState title="Nothing saved yet" hint="Bookmark residences from the marketplace to build your shortlist." />
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {saved.map((p) => (
                <div key={p.id} className="relative" data-testid={`saved-card-${p.id}`}>
                  <div className="border border-black/10 [&_a]:border-black/10 [&_a]:bg-white [&_h3]:text-black [&_p]:text-black">
                    <PropertyCard property={p} />
                  </div>
                  <button
                    onClick={() => unsave(p.id)}
                    data-testid={`unsave-${p.id}`}
                    className="absolute right-3 top-3 z-10 flex items-center gap-1 bg-black/80 px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-widest text-white backdrop-blur hover:bg-red-900"
                  >
                    <Trash2 size={11} /> Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "enquiries" && (
        <div className="dash-card overflow-x-auto" data-testid="enquiries-panel">
          {enquiries.length === 0 ? (
            <EmptyState title="No enquiries yet" hint={<Link to="/listings" className="text-black underline">Browse residences</Link>} />
          ) : (
            <table className="w-full min-w-[720px]">
              <thead><tr><Th>Residence</Th><Th>Your Note</Th><Th>Handled By</Th><Th>Status & Visits</Th></tr></thead>
              <tbody>
                {enquiries.map((l) => {
                  const sv = l.site_visit || {};
                  return (
                    <tr key={l.id} data-testid={`enquiry-row-${l.id}`}>
                      <Td className="font-medium text-black">{l.property_title}<p className="text-[11px] text-black/45">{l.property_locality}</p></Td>
                      <Td className="max-w-[220px] italic text-black/55">"{l.message || "—"}"</Td>
                      <Td>{l.assigned_to_name || "Routing…"}</Td>
                      <Td>
                        <span className={`px-2 py-1 font-mono text-[9px] uppercase tracking-widest ${LEAD_STATUS_COLORS[l.status]}`}>{LEAD_STATUS_LABELS[l.status]}</span>
                        {sv.status === "confirmed" && sv.confirmed && (
                          <p className="mt-2 flex items-center gap-1.5 text-[11px] font-medium text-cyan-900" data-testid={`visit-confirmed-${l.id}`}>
                            <CalendarCheck size={12} /> Visit confirmed · {fmtSlot(sv.confirmed.slot)}
                          </p>
                        )}
                        <BuyerRescheduleControl lead={l} onDone={reload} />
                        {sv.status === "proposed" && sv.proposed?.length > 0 && (
                          <div className="mt-2 space-y-1.5" data-testid={`visit-slots-${l.id}`}>
                            <p className="font-mono text-[9px] uppercase tracking-widest text-black/45">Proposed slots — tap to confirm:</p>
                            {sv.proposed.map((p) => (
                              <button
                                key={p.slot}
                                onClick={() => confirmVisit(l.id, p.slot)}
                                data-testid={`visit-confirm-${l.id}-${p.slot}`}
                                className="dash-btn-ghost mr-1.5"
                              >
                                <CalendarCheck size={11} /> {fmtSlot(p.slot)}
                              </button>
                            ))}
                          </div>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "visits" && <VisitCalendar leads={enquiries} />}

      {tab === "my-listings" && (
        <div data-testid="buyer-listings">
          {formState !== null ? (
            <ListingForm
              key={formState === "new" ? "new" : formState.id}
              initial={formState === "new" ? null : formState}
              onDone={() => { setFormState(null); loadListings(); loadStats(); }}
              onCancel={() => setFormState(null)}
            />
          ) : (
            <>
              <div className="mb-4 flex items-center justify-between">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/45">Sell or lease your own property — every listing is reviewed by our sales desk before it goes live</p>
                <button className="dash-btn" onClick={() => setFormState("new")} data-testid="buyer-add-listing-button">
                  <PlusCircle size={13} /> Add Listing
                </button>
              </div>
              <div className="dash-card overflow-x-auto">
                {listings.length === 0 ? <EmptyState title="No listings yet" hint="Have a property to sell or lease? Add it — the sales desk reviews and publishes it." /> : (
                  <table className="w-full min-w-[640px]">
                    <thead><tr><Th>Listing</Th><Th>Price</Th><Th>Status</Th><Th></Th></tr></thead>
                    <tbody>
                      {listings.map((p) => (
                        <tr key={p.id} data-testid={`buyer-listing-row-${p.id}`}>
                          <Td><p className="font-medium text-black">{p.title}</p><p className="text-[11px] text-black/45">{p.type} · {p.locality}</p>
                            {p.status === "rejected" && p.rejection_reason && <p className="mt-1 text-[11px] text-red-600">Rejected: {p.rejection_reason}</p>}
                          </Td>
                          <Td className="font-medium">{formatINR(p.price, p.price_unit)}</Td>
                          <Td><span className={`px-2 py-1 font-mono text-[9px] uppercase tracking-widest ${LISTING_STATUS_COLORS[p.status]}`}>{p.status.replace("_", " ")}</span></Td>
                          <Td><button className="dash-btn-ghost" data-testid={`buyer-edit-listing-${p.id}`} onClick={() => setFormState(p)}>Edit</button></Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
